import {
  Box,
  AspectRatio,
  Checkbox as ChakraCheckbox,
  CheckboxGroup as ChakraCheckboxGroup,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { useId, type ReactNode } from "react";

import { asSlotComponent } from "../internal/v3-slot";
import { Image } from "../image/image";

const CheckboxRoot = asSlotComponent(ChakraCheckbox.Root);
const CheckboxControl = asSlotComponent(ChakraCheckbox.Control);
const CheckboxIndicator = asSlotComponent(ChakraCheckbox.Indicator);
const CheckboxLabel = asSlotComponent(ChakraCheckbox.Label);
const CheckboxHiddenInput = asSlotComponent(ChakraCheckbox.HiddenInput);
const CheckboxGroupRoot = asSlotComponent(ChakraCheckboxGroup);

export type CheckboxProps = HTMLChakraProps<"label"> & {
  value?: string;
  checked?: boolean | "indeterminate";
  defaultChecked?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  indeterminate?: boolean;
  onCheckedChange?: (details: { checked: boolean | "indeterminate" }) => void;
  size?: string | Record<string, string> | Array<string | null>;
  imageSrc?: string;
  imageAlt?: string;
  children?: ReactNode;
  name?: string;
};

function Checkbox({
  imageSrc,
  imageAlt,
  children,
  colorPalette = "blue",
  indeterminate,
  checked,
  ...rest
}: CheckboxProps) {
  const reactId = useId();
  const ids = {
    root: `${reactId}-root`,
    hiddenInput: `${reactId}-input`,
    control: `${reactId}-control`,
    label: `${reactId}-label`
  };
  // v3 expresses the indeterminate icon via checked="indeterminate".
  const resolvedChecked = indeterminate ? "indeterminate" : checked;
  const checkbox = (
    <CheckboxRoot
      colorPalette={colorPalette}
      ids={ids}
      checked={resolvedChecked}
      {...rest}
    >
      <CheckboxHiddenInput />
      <CheckboxControl>
        <CheckboxIndicator />
      </CheckboxControl>
      {children ? <CheckboxLabel>{children}</CheckboxLabel> : null}
    </CheckboxRoot>
  );

  if (imageSrc) {
    return (
      <Box maxW="36rem">
        <AspectRatio ratio={16 / 9} mb={2}>
          <Image
            src={imageSrc}
            alt={imageAlt}
            aria-hidden="true"
            magnify={true}
            h="100%"
            w="100%"
          />
        </AspectRatio>
        {checkbox}
      </Box>
    );
  }

  return <Box maxW="36rem">{checkbox}</Box>;
}

export type CheckboxGroupProps = Record<string, unknown> & {
  children?: ReactNode;
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  disabled?: boolean;
  invalid?: boolean;
  name?: string;
};

const CheckboxGroup = CheckboxGroupRoot as (
  props: CheckboxGroupProps
) => JSX.Element;

export { Checkbox, CheckboxGroup };
